import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminSitemapChecks } from "@/lib/admin-sitemap-checks.functions";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/sitemap-checks")({
  head: () => ({
    meta: [
      { title: "Admin — Sitemap-checks" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SitemapChecksPage,
});

function fmt(ts: string | null) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "medium" });
}

interface BadStatus { url: string; status: number }
interface Redirect { url: string; status: number; location?: string }
interface Run {
  id: string;
  ran_at: string;
  total_urls: number;
  ok: boolean;
  error: string | null;
  missing_vacancies: string[];
  bad_status: BadStatus[];
  redirects: Redirect[];
  robots_issues: string[];
  excluded_hits: string[];
}

function SitemapChecksPage() {
  const fetchRuns = useServerFn(adminSitemapChecks);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "sitemap-checks"],
    queryFn: () => fetchRuns(),
    refetchInterval: 30_000,
  });

  const runs = (data?.runs ?? []) as unknown as Run[];
  const vacMissingCount = (r: Run) =>
    r.missing_vacancies.filter((u) => u.includes("/werken-bij/")).length;

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Sitemap-checks</h1>
          <p className="text-sm text-muted-foreground">
            Automatische controle van sitemap.xml, robots.txt en vacature-URLs. Faalt bij missende slugs,
            foutstatuscodes, 3xx-redirects of uitgesloten routes — verstuurt alert-mail.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          disabled={isFetching}
        >
          {isFetching ? "Verversen…" : "Verversen"}
        </button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Laden…</p>}
      {error && <p className="text-sm text-red-600">Kon runs niet laden: {(error as Error).message}</p>}

      {data && (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Uitgevoerd</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">URLs</th>
                <th className="px-3 py-2 text-right">Missende vacatures</th>
                <th className="px-3 py-2 text-right">Foutcodes</th>
                <th className="px-3 py-2 text-right">Redirects</th>
                <th className="px-3 py-2 text-right">robots.txt</th>
                <th className="px-3 py-2 text-right">Uitgesloten in sitemap</th>
                <th className="px-3 py-2">Details</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">
                    Nog geen runs.
                  </td>
                </tr>
              )}
              {runs.map((r) => (
                <tr key={r.id} className="border-t align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(r.ran_at)}</td>
                  <td className="px-3 py-2">
                    {r.ok ? (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                        <CheckCircle2 className="h-3 w-3" /> OK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                        <AlertTriangle className="h-3 w-3" /> Faalt
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{r.total_urls}</td>
                  <td className={`px-3 py-2 text-right ${vacMissingCount(r) ? "text-red-600 font-semibold" : ""}`}>
                    {vacMissingCount(r)}
                  </td>
                  <td className={`px-3 py-2 text-right ${r.bad_status.length ? "text-red-600 font-semibold" : ""}`}>
                    {r.bad_status.length}
                  </td>
                  <td className={`px-3 py-2 text-right ${r.redirects.length ? "text-amber-600 font-semibold" : ""}`}>
                    {r.redirects.length}
                  </td>
                  <td className={`px-3 py-2 text-right ${r.robots_issues.length ? "text-red-600 font-semibold" : ""}`}>
                    {r.robots_issues.length}
                  </td>
                  <td className={`px-3 py-2 text-right ${r.excluded_hits.length ? "text-red-600 font-semibold" : ""}`}>
                    {r.excluded_hits.length}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.error && <div className="text-red-600">{r.error}</div>}
                    {r.missing_vacancies.length > 0 && (
                      <details>
                        <summary className="cursor-pointer text-red-700">Missende vacatures</summary>
                        <ul className="mt-1 space-y-0.5 font-mono">
                          {r.missing_vacancies.map((u) => <li key={u}>• {u}</li>)}
                        </ul>
                      </details>
                    )}
                    {r.bad_status.length > 0 && (
                      <details>
                        <summary className="cursor-pointer text-red-700">Foutcodes</summary>
                        <ul className="mt-1 space-y-0.5 font-mono">
                          {r.bad_status.map((b) => <li key={b.url}>• [{b.status}] {b.url}</li>)}
                        </ul>
                      </details>
                    )}
                    {r.redirects.length > 0 && (
                      <details>
                        <summary className="cursor-pointer text-amber-700">Redirects</summary>
                        <ul className="mt-1 space-y-0.5 font-mono">
                          {r.redirects.map((rd) => (
                            <li key={rd.url}>• [{rd.status}] {rd.url}{rd.location ? ` → ${rd.location}` : ""}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                    {r.robots_issues.length > 0 && (
                      <details>
                        <summary className="cursor-pointer text-red-700">robots.txt</summary>
                        <ul className="mt-1 space-y-0.5">
                          {r.robots_issues.map((x) => <li key={x}>• {x}</li>)}
                        </ul>
                      </details>
                    )}
                    {r.excluded_hits.length > 0 && (
                      <details>
                        <summary className="cursor-pointer text-red-700">Uitgesloten routes</summary>
                        <ul className="mt-1 space-y-0.5 font-mono">
                          {r.excluded_hits.map((u) => <li key={u}>• {u}</li>)}
                        </ul>
                      </details>
                    )}
                    {r.ok && !r.error && <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
